import React from 'react';
import { Fade, Grid, Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import toast from 'react-hot-toast';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Quiz as QuizIcon,
  MeetingRoom as MeetingRoomIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ROLES } from '../../../constants';
import { getUserDetails } from '../../../utils';
import { BarChart } from '../../../sections/@dashboard/app';

const OverviewTab = ({ data = {}, msCsData = [] }) => {
  const theme = useTheme();
  const userDetails = getUserDetails();
  const isAdminOrSiteLeader = [ROLES.ADMIN, ROLES.SITE_LEADER].includes(userDetails?.role);
  
  // Generate last 6 months labels
  const getLast6Months = () => {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push(`${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`);
    }
    return months;
  };

  // Prepare trend data - use actual data from API
  const trendData = data?.overviewTrends || {};
  const months = getLast6Months();
  
  // Use actual API data if available, otherwise show empty/zero data
  const usersTrend = Array.isArray(trendData.users) && trendData.users.length === 6
    ? trendData.users
    : months.map(() => 0);
  
  const meetingsTrend = Array.isArray(trendData.meetings) && trendData.meetings.length === 6
    ? trendData.meetings
    : months.map(() => 0);
  
  const surveysTrend = Array.isArray(trendData.surveys) && trendData.surveys.length === 6
    ? trendData.surveys
    : months.map(() => 0);

  const trendChartData = {
    options: {
      chart: {
        type: 'line',
        height: 350,
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
        },
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      markers: {
        size: 5,
        hover: {
          size: 8,
        },
      },
      xaxis: {
        categories: months,
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: theme.palette.text.secondary,
          },
        },
      },
      yaxis: {
        title: {
          text: 'Count',
          style: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
        },
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: theme.palette.text.secondary,
          },
        },
      },
      colors: [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main],
      legend: {
        position: 'top',
        fontSize: '14px',
        fontWeight: 500,
        horizontalAlign: 'right',
        offsetY: 10,
        offsetX: 0,
        itemMargin: {
          horizontal: 15,
          vertical: 5,
        },
        floating: false,
      },
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 3,
        padding: {
          top: 50,
          right: 10,
          bottom: 0,
          left: 10,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: theme.palette.mode,
        y: {
          formatter: (val) => val.toLocaleString(),
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100],
        },
      },
    },
    series: [
      {
        name: 'Users',
        data: usersTrend,
      },
      {
        name: 'Sessions',
        data: meetingsTrend,
      },
      {
        name: 'Surveys',
        data: surveysTrend,
      },
    ],
  };

  const stats = [
    {
      key: 'totalUsers',
      label: 'Total Users',
      value: data.totalUsers || 0,
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: 'primary',
    },
    {
      key: 'totalTeachers',
      label: 'Teachers',
      value: data.totalTeachers || 0,
      icon: <PersonIcon sx={{ fontSize: 32 }} />,
      color: 'info',
    },
    {
      key: 'totalEdifiers',
      label: 'Edifiers',
      value: data.totalEdifiers || 0,
      icon: <SupervisorAccountIcon sx={{ fontSize: 32 }} />,
      color: 'success',
    },
    {
      key: 'totalSiteLeaders',
      label: 'Site Leaders',
      value: data.totalSiteLeaders || 0,
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      color: 'warning',
    },
    {
      key: 'totalSchools',
      label: 'Schools',
      value: data.totalSchools || 0,
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
      color: 'secondary',
      show: data.totalSchools !== undefined,
    },
    {
      key: 'totalSurveys',
      label: 'Surveys',
      value: data.totalSurveys || 0,
      icon: <QuizIcon sx={{ fontSize: 32 }} />,
      color: 'error',
    },
    {
      key: 'totalMeetings',
      label: 'Sessions',
      value: data.totalMeetings || 0,
      icon: <MeetingRoomIcon sx={{ fontSize: 32 }} />,
      color: 'primary',
    },
  ].filter((stat) => stat.show !== false);

  return (
    <Fade in timeout={500}>
      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={stat.key}>
            <Card
              sx={{
                p: 2.5,
                background: (theme) => theme.palette.background.paper,
                boxShadow: (theme) => theme.customShadows.card,
                borderRadius: 2,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: (theme) => theme.customShadows.z8,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${stat.color}.lighter`,
                    color: `${stat.color}.main`,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}

        {/* Latest Trends Chart */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 12px 0px rgba(13, 10, 44, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    mr: 2,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                    Latest Trends (Last 6 Months)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Track growth across users, sessions, and surveys
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box 
              sx={{ 
                position: 'relative',
                '& .apexcharts-toolbar': {
                  top: '0px !important',
                  right: '10px !important',
                  zIndex: 1,
                },
                '& .apexcharts-legend': {
                  paddingTop: '40px !important',
                  paddingRight: '60px !important',
                },
              }}
            >
              <Chart
                options={trendChartData.options}
                series={trendChartData.series}
                type="line"
                height={350}
              />
            </Box>
          </Card>
        </Grid>

        {isAdminOrSiteLeader && Array.isArray(msCsData) && msCsData.length > 0 && (
          <Grid item xs={12}>
            <BarChart data={msCsData} toast={toast} />
          </Grid>
        )}
      </Grid>
    </Fade>
  );
};

export default OverviewTab;

